import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  ArrowRight, Check, Upload, FileText, Clock, DollarSign,
  CheckCircle2, Loader2, AlertCircle, History
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const PRICE_PER_YEAR = 350;

const BackOnTrackTab = ({ businessId, token }) => {
  const { toast } = useToast();
  const [selectedYears, setSelectedYears] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [uploadYear, setUploadYear] = useState(null);
  const [uploadingOrder, setUploadingOrder] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(null); // SSE progress state

  const authHeaders = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - 1 - i);

  const fetchOrders = useCallback(async () => {
    if (!businessId || !token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/mybookie/backontrack/orders/${businessId}`, authHeaders);
      setOrders(res.data.orders || []);
    } catch (error) {
    }
    setLoading(false);
  }, [businessId, token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const toggleYear = (year) => {
    setSelectedYears(prev => 
      prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year].sort()
    );
  };

  const handleCheckout = async () => {
    if (selectedYears.length === 0) return;
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/api/mybookie/backontrack/checkout`, {
        years: selectedYears,
        business_id: businessId,
        origin_url: window.location.origin
      }, authHeaders);
      window.location.href = res.data.url;
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to start checkout", variant: "destructive" });
    }
    setCheckoutLoading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingOrder || !uploadYear) return;
    
    setProcessing(true);
    setProgress({ current: 0, total: 0, imported: 0, expenses: 0, income: 0 });
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = btoa(event.target.result);
        try {
          const response = await fetch(
            `${API}/api/mybookie/backontrack/process-stream/${uploadingOrder.id}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ csv_data: base64, year: uploadYear }),
            }
          );
          const streamReader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (true) {
            const { done, value } = await streamReader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'progress') {
                  setProgress({
                    current: data.current, total: data.total,
                    imported: data.imported, expenses: data.expenses, income: data.income,
                  });
                } else if (data.type === 'start') {
                  setProgress(prev => ({ ...prev, total: data.total }));
                } else if (data.type === 'complete') {
                  toast({ 
                    title: "Transactions Imported", 
                    description: `${data.imported} transactions (${data.expenses} expenses, ${data.income} income) for ${uploadYear}` 
                  });
                  setUploadYear(null);
                  setUploadingOrder(null);
                  setProgress(null);
                  fetchOrders();
                }
              } catch (err) { console.error("SSE parse error:", err); /* skip malformed SSE lines */ }
            }
          }
        } catch (error) {
          toast({ title: "Import Failed", description: error.response?.data?.detail || "CSV parsing error", variant: "destructive" });
        }
        setProcessing(false);
      };
      reader.readAsBinaryString(file);
    } catch (error) {
      setProcessing(false);
      setProgress(null);
      toast({ title: "Error", description: "Failed to read file", variant: "destructive" });
    }
  };

  const paidYears = orders
    .filter(o => ["paid", "processing", "completed"].includes(o.status))
    .flatMap(o => o.years);

  const fmt = (n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="backontrack-tab">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0A2B1D] to-[#1A3D2E] rounded-xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <History className="w-6 h-6 text-[#9CC4AF]" />
          </div>
          <div>
            <h2 className="text-xl font-bold mb-1">Back on Track</h2>
            <p className="text-[#9CC4AF] text-sm">
              Behind on your books? Upload past bank statements and we'll auto-categorize everything — 
              giving you clean financials in minutes, not months.
            </p>
            <div className="flex items-center gap-4 mt-3 text-xs text-[#B4C9BE]">
              <span className="flex items-center gap-1"><Check className="w-3 h-3" /> AI auto-categorization</span>
              <span className="flex items-center gap-1"><Check className="w-3 h-3" /> P&L reports generated</span>
              <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Tax-ready data</span>
            </div>
          </div>
        </div>
      </div>

      {/* Year Selection */}
      <Card data-testid="year-selection-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#0A2B1D]" />
            Select Years to Catch Up
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            {availableYears.map(year => {
              const isPaid = paidYears.includes(year);
              const isSelected = selectedYears.includes(year);
              return (
                <button
                  key={year}
                  onClick={() => !isPaid && toggleYear(year)}
                  disabled={isPaid}
                  className={`relative p-4 rounded-lg border-2 text-center transition-all ${
                    isPaid
                      ? 'border-green-300 bg-green-50 cursor-default'
                      : isSelected
                        ? 'border-[#0A2B1D] bg-[#E2ECE7] shadow-sm'
                        : 'border-[#E6E8E6] hover:border-[#3A6E53] hover:bg-[#F6F5F2]'
                  }`}
                  data-testid={`year-btn-${year}`}
                >
                  <p className={`text-lg font-bold ${isPaid ? 'text-green-700' : isSelected ? 'text-[#0A2B1D]' : 'text-[#111614]'}`}>
                    {year}
                  </p>
                  {isPaid ? (
                    <p className="text-xs text-green-600 mt-1 flex items-center justify-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Purchased
                    </p>
                  ) : (
                    <p className="text-xs text-[#8A948F] mt-1">{fmt(PRICE_PER_YEAR)}</p>
                  )}
                  {isSelected && !isPaid && (
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#0A2B1D] rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {selectedYears.length > 0 && (
            <div className="flex items-center justify-between bg-[#F6F5F2] rounded-lg p-4 border border-[#E6E8E6]">
              <div>
                <p className="text-sm text-[#5C6661]">
                  {selectedYears.length} year{selectedYears.length > 1 ? 's' : ''} selected: {selectedYears.join(', ')}
                </p>
                <p className="text-xl font-bold text-[#0A2B1D] mt-1">
                  {fmt(selectedYears.length * PRICE_PER_YEAR)}
                  <span className="text-sm font-normal text-[#8A948F] ml-2">{fmt(PRICE_PER_YEAR)}/year</span>
                </p>
              </div>
              <Button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="bg-[#0A2B1D] hover:bg-[#1A3D2E] text-white px-6"
                data-testid="checkout-btn"
              >
                {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-1" />}
                Purchase
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Orders */}
      {orders.length > 0 && (
        <Card data-testid="orders-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#0A2B1D]" />
              Your Back on Track Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {orders.map(order => (
                <div key={order.id} className="border border-[#E6E8E6] rounded-lg p-4" data-testid={`order-${order.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-semibold text-[#111614]">
                        Years: {order.years.join(', ')}
                      </p>
                      <p className="text-xs text-[#8A948F]">
                        {new Date(order.created_at).toLocaleDateString()} &middot; {fmt(order.amount)}
                      </p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      order.status === 'completed' ? 'bg-green-100 text-green-700' :
                      order.status === 'processing' ? 'bg-amber-100 text-amber-700' :
                      order.status === 'paid' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {order.status === 'paid' ? 'Ready for Upload' : 
                       order.status === 'processing' ? 'In Progress' :
                       order.status === 'completed' ? 'Completed' : 
                       order.status}
                    </span>
                  </div>

                  {(order.status === 'paid' || order.status === 'processing') && (
                    <div className="space-y-2">
                      {order.years.map(year => {
                        const processed = order.processed_years?.includes(year);
                        const result = order[`results_${year}`];
                        return (
                          <div key={year} className={`flex items-center justify-between p-3 rounded-lg ${processed ? 'bg-green-50' : 'bg-[#F6F5F2]'}`}>
                            <div className="flex items-center gap-2">
                              {processed ? (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              ) : (
                                <Upload className="w-4 h-4 text-[#0A2B1D]" />
                              )}
                              <span className="text-sm font-medium">{year}</span>
                              {result && (
                                <span className="text-xs text-[#8A948F]">
                                  {result.total_imported} transactions imported
                                </span>
                              )}
                            </div>
                            {!processed && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setUploadYear(year); setUploadingOrder(order); }}
                                className="text-xs h-7"
                                data-testid={`upload-btn-${year}`}
                              >
                                <Upload className="w-3 h-3 mr-1" /> Upload CSV
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* How It Works */}
      <Card className="bg-[#F6F5F2] border-[#E6E8E6]">
        <CardContent className="p-6">
          <h3 className="text-base font-semibold text-[#111614] mb-4">How It Works</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { step: '1', title: 'Select Years', desc: 'Choose which years you need to catch up on' },
              { step: '2', title: 'Upload Statements', desc: 'Upload CSV bank statements for each year' },
              { step: '3', title: 'Instant Books', desc: 'AI categorizes everything — view clean reports immediately' }
            ].map(item => (
              <div key={item.step} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#0A2B1D] text-white flex items-center justify-center text-xs font-bold shrink-0">
                  {item.step}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#111614]">{item.title}</p>
                  <p className="text-xs text-[#5C6661] mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={!!uploadYear} onOpenChange={(open) => { if (!open) { setUploadYear(null); setUploadingOrder(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Bank Statement — {uploadYear}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-[#F6F5F2] rounded-lg p-4 text-sm text-[#5C6661]">
              <p className="font-medium text-[#111614] mb-2">CSV Format Requirements:</p>
              <ul className="space-y-1 text-xs">
                <li className="flex items-start gap-2"><Check className="w-3 h-3 mt-0.5 text-green-600 shrink-0" /> Columns: Date, Description, Amount (or Debit/Credit)</li>
                <li className="flex items-start gap-2"><Check className="w-3 h-3 mt-0.5 text-green-600 shrink-0" /> Most bank CSV exports work out of the box</li>
                <li className="flex items-start gap-2"><Check className="w-3 h-3 mt-0.5 text-green-600 shrink-0" /> Transactions are auto-categorized by AI</li>
              </ul>
            </div>
            
            {processing ? (
              <div className="py-6 space-y-4" data-testid="processing-progress">
                {progress && progress.total > 0 ? (
                  <>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#5C6661]">Processing transactions...</span>
                        <span className="font-medium text-[#111614]">{Math.round((progress.current / progress.total) * 100)}%</span>
                      </div>
                      <div className="h-3 bg-[#E6E8E6] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#0A2B1D] rounded-full transition-all duration-300"
                          style={{ width: `${(progress.current / progress.total) * 100}%` }}
                          data-testid="progress-bar"
                        />
                      </div>
                      <p className="text-xs text-[#8A948F] text-center">{progress.current} / {progress.total} rows</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="p-2 bg-[#F0F5F2] rounded">
                        <p className="font-semibold text-[#111614]">{progress.imported}</p>
                        <p className="text-[#8A948F]">Imported</p>
                      </div>
                      <div className="p-2 bg-red-50 rounded">
                        <p className="font-semibold text-red-600">{progress.expenses}</p>
                        <p className="text-[#8A948F]">Expenses</p>
                      </div>
                      <div className="p-2 bg-green-50 rounded">
                        <p className="font-semibold text-green-600">{progress.income}</p>
                        <p className="text-[#8A948F]">Income</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-[#0A2B1D] mr-3" />
                    <span className="text-sm text-[#5C6661]">Preparing...</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <label 
                  className="w-full border-2 border-dashed border-[#B4C9BE] rounded-lg p-8 text-center cursor-pointer hover:border-[#0A2B1D] hover:bg-[#F6F5F2] transition-colors"
                  data-testid="csv-upload-area"
                >
                  <Upload className="w-8 h-8 text-[#B4C9BE] mx-auto mb-2" />
                  <p className="text-sm font-medium text-[#111614]">Click to upload CSV</p>
                  <p className="text-xs text-[#8A948F] mt-1">Bank statement for {uploadYear}</p>
                  <input 
                    type="file" 
                    accept=".csv" 
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="csv-file-input"
                  />
                </label>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BackOnTrackTab;
